import React from "react";
import { Activity } from "lucide-react";

/**
 * <LLMProvenanceBadge analysis={...} />
 *
 * Shows a small "Powered by <model>" footnote ONLY when the verdict was
 * NOT generated by the primary provider (OpenRouter / DeepSeek). Transparency move:
 * users with a sharp eye notice the slight reasoning-style differences
 * across LLMs, and we want them to know we have multi-provider
 * resilience instead of secretly serving them a different model.
 *
 * The badge is intentionally subtle (text-overline, muted colour). It
 * is NOT shown for primary-provider verdicts because that's the calibration
 * baseline and adding a "Powered by X" footnote to every verdict
 * would feel like a sponsored ad.
 *
 * Maps internal provider keys to display labels:
 *   anthropic → (no badge)
 *   gemini    → "Gemini 2.5 Pro"
 *   openai    → "OpenAI <model>"
 *   anything-else → titlecased
 *
 * Props:
 *   analysis: { llm_provider?: string, llm_model?: string }  — usually
 *             the analysis result object
 *   variant?: "inline" | "compact"   — `compact` skips the icon for
 *             tight layouts (e.g. the share card)
 */
const PROVIDER_LABELS = {
    anthropic: { display: "AI (primary)", primary: true },
    gemini: { display: "Gemini 2.5 Pro", primary: false },
    openai: { display: "OpenAI", primary: false },
};

export default function LLMProvenanceBadge({ analysis, variant = "inline" }) {
    if (!analysis) return null;
    const provider = (analysis.llm_provider || "").toLowerCase();
    if (!provider) return null;
    const meta = PROVIDER_LABELS[provider];
    // Primary provider = baseline → no badge (would feel like an ad on every verdict).
    if (!meta || meta.primary) return null;

    const label = meta.display;
    const compact = variant === "compact";

    return (
        <p
            className="text-overline mt-1.5 inline-flex items-center gap-1.5"
            style={{
                fontSize: "0.55rem",
                color: "hsl(var(--text-muted))",
                letterSpacing: "0.06em",
            }}
            data-testid="llm-provenance-badge"
            data-llm-provider={provider}
            title={`Primary model unavailable — auto-rotated to ${label} (no quality compromise; this is part of our multi-provider resilience architecture).`}
        >
            {!compact && <Activity size={9} strokeWidth={1.6} aria-hidden="true" />}
            <span>Powered by {label} · fallback</span>
        </p>
    );
}
